import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { registerConfirmHandler } from "../utils/appNotify";
import {
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../utils/formModalStyles";

/**
 * Centered compact confirmation dialog (replaces browser confirm / side panel).
 * Mount once near app root.
 */
const AppConfirmProvider = ({ children }) => {
  const [state, setState] = useState({
    open: false,
    message: "",
    title: "Confirm",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
    tone: "primary", // primary | error | warning
  });
  const resolverRef = useRef(null);

  const closeWith = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const requestConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        message: String(message ?? ""),
        title: options.title || "Confirm",
        confirmLabel: options.confirmLabel || "Confirm",
        cancelLabel: options.cancelLabel || "Cancel",
        tone: options.tone || "primary",
      });
    });
  }, []);

  useEffect(() => {
    registerConfirmHandler(requestConfirm);
    return () => registerConfirmHandler(null);
  }, [requestConfirm]);

  useEffect(() => {
    if (!state.open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeWith(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [state.open, closeWith]);

  const confirmColor =
    state.tone === "error"
      ? "error"
      : state.tone === "warning"
        ? "warning"
        : "primary";

  return (
    <>
      {children}
      <Dialog
        open={state.open}
        onClose={() => closeWith(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: "1.05rem" }}>
          {state.title}
        </DialogTitle>
        <DialogContent sx={{ pt: "4px !important", pb: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {state.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1, px: 2, pb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => closeWith(false)}
            sx={FORM_CANCEL_BUTTON_SX}
          >
            {state.cancelLabel}
          </Button>
          <Button
            size="small"
            variant="contained"
            color={confirmColor}
            onClick={() => closeWith(true)}
            sx={FORM_SUBMIT_BUTTON_SX}
          >
            {state.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppConfirmProvider;
